import { z } from "zod";

import { emailSchema, stepUpFields } from "./auth";

/**
 * Güvenlik parametreleri ve e-posta değişikliği sözleşmeleri (CLOS-225 `[048]`).
 *
 * ### Açık kararın cevabı
 *
 * Kart *"ek kimlik doğrulama yöntemi ve varsa güvenlik bekleme süresi konfigüre
 * edilecektir"* diyor ve *"parametreler hard-code edilmez"* kuralını getiriyor. Değerler
 * `config/security.json`'da; burada yalnız o dosyanın ŞEKLİ tanımlı.
 *
 * ⚠ Konfigürasyon DOĞRULANIYOR: elle yazılan bir JSON'daki yazım hatası sessizce sıfır
 * ömürlü bir token ya da kapalı bir yeniden doğrulama demek olurdu ve arızayı ilk
 * kullanıcı keşfederdi.
 */

/**
 * Yeniden doğrulama yöntemleri.
 *
 * Bugün tek yöntem var: mevcut parola. Kullanılmayan bir yöntemi listeye eklemek, hiçbir
 * kodun uygulamadığı bir güvenlik sözü olurdu (ADR-0014). 2FA bu kümede DEĞİL çünkü o bir
 * alternatif değil, etkinken parolaya EKLENEN ikinci bir kanıt — kart onu kanonik kural
 * olarak tanımlıyor, parametre olarak değil.
 */
export const REAUTHENTICATION_METHODS = ["password"] as const;
export type ReauthenticationMethod = (typeof REAUTHENTICATION_METHODS)[number];

export const securityConfigSchema = z.object({
  emailChange: z.object({
    reauthentication: z.enum(REAUTHENTICATION_METHODS),
    /** Yeni adrese giden doğrulama bağlantısının ömrü. */
    tokenTtlMinutes: z.number().int().positive(),
    /** İki TALEP arasındaki asgari süre; `0` = bekleme yok. */
    coolDownMinutes: z.number().int().nonnegative(),
  }),
  /**
   * Kişisel veri talepleri (CLOS-222 `[201]` açık kararı — "parametreler config'te").
   * Kanonik veri sahipliğini, hukuki saklamayı ya da audit bütünlüğünü DEĞİŞTİREMEZ
   * (kartın CLOS-216 ile paylaştığı sınır); burada yaşayan yalnız SÜRELER.
   */
  privacy: z.object({
    /** Talep oluşturma/indirme öncesi yeniden doğrulama yöntemi. */
    reauthentication: z.enum(REAUTHENTICATION_METHODS),
    /** Export dosyasının kısa ömürlü indirme penceresi. */
    exportDownloadTtlHours: z.number().int().positive(),
    /** Aynı türde ikinci bir talep açılabilmesi için önceki talebin bitmiş olması yetmez;
     * bu bekleme süresi de geçmeli — art arda tetiklenen taleplerin işleme yükünü sınırlar. */
    requestCoolDownMinutes: z.number().int().nonnegative(),
  }),
});
export type SecurityConfig = z.infer<typeof securityConfigSchema>;

/**
 * E-posta değişikliği TALEBİ.
 *
 * Parola her zaman isteniyor: oturumun varlığı yetmez, oturum çalınmış olabilir ve giriş
 * adresini değiştirmek tam da saldırganın hesabı kalıcı olarak ele geçirmek için yapacağı
 * şeydir (`[019]`'un yeniden doğrulama gerekçesiyle aynı).
 */
export const requestEmailChangeSchema = z.object({
  newEmail: emailSchema,
  currentPassword: z
    .string({ error: "Mevcut şifrenizi giriniz" })
    .min(1, "Mevcut şifrenizi giriniz"),
  /** 2FA etkinse zorunlu; değilse gönderilmez (kartın çalışma zamanı koşulu). */
  code: z.string().optional(),
  recoveryCode: z.string().optional(),
});
export type RequestEmailChangeInput = z.infer<typeof requestEmailChangeSchema>;

/**
 * Talebin sonucu.
 *
 * `pendingInvitations`: eski adrese bağlı bekleyen davet SAYISI. Kart
 * *"bekleyen davet ve üyelik ilişkileri kontrollü biçimde değerlendirilir"* diyor ve
 * `[036]` *"eski veya yeni e-postaya bağlı davetlerin yaşam döngüsü SESSİZCE
 * DEĞİŞTİRİLMEZ"* diye ekliyor. Bu yüzden davetler taşınmıyor; kullanıcıya kaç davetin
 * eski adreste kalacağı SÖYLENİYOR ve kararı o veriyor.
 */
export const emailChangeRequestedSchema = z.object({
  newEmail: z.string(),
  expiresAt: z.string(),
  pendingInvitations: z.number().int().nonnegative(),
});
export type EmailChangeRequested = z.infer<typeof emailChangeRequestedSchema>;

/**
 * Hesap kapatma onayı (CLOS-69 `[049]`).
 *
 * ⚠ İKİ kanıt: parola VE açık bir onay bayrağı. Yalnız parola isteseydik, başka bir
 * amaçla açılmış bir formun yanlışlıkla gönderilmesi hesabı kapatabilirdi; yalnız bayrak
 * isteseydik çalınmış bir oturum yeterdi. Geri alınamaz bir işlem iki kanıt hak ediyor.
 */
export const closeAccountSchema = z.object({
  currentPassword: z
    .string({ error: "Mevcut şifrenizi giriniz" })
    .min(1, "Mevcut şifrenizi giriniz"),
  confirm: z.literal(true, { error: "Hesabınızı kapatmayı onaylamalısınız" }),
  /**
   * ⚠ ÜÇÜNCÜ kanıt, 2FA etkinse (CLOS-520 · CLOS-214 `[080]`). Geri alınamaz bir işlemde
   * ikinci faktörü atlamak, korumayı tam da en pahalı işlemde devre dışı bırakmak olurdu.
   * Alan opsiyonel çünkü zorunluluk hesabın gerçek 2FA durumuna bağlı — bkz. `stepUpFields`.
   */
  ...stepUpFields,
});
export type CloseAccountInput = z.infer<typeof closeAccountSchema>;

export const confirmEmailChangeSchema = z.object({
  token: z
    .string({ error: "Doğrulama bağlantısı geçersiz" })
    .min(1, "Doğrulama bağlantısı geçersiz"),
});
export type ConfirmEmailChangeInput = z.infer<typeof confirmEmailChangeSchema>;
